/**
 * Ledger-based aging (ACC-09 / ACC-10 fix, 2026-09-30).
 *
 * WHY: AR / AP aging used to be computed from documents — each invoice's own
 * `paidAmount`, or a FIFO of receipts/payments against only the still-open
 * invoices. That drifted from the customer/vendor ledger whenever money moved
 * without touching an invoice (returns, opening balances, journal vouchers,
 * on-account receipts, WHT, bad-debt write-offs) and AP aging deducted payments
 * twice (payments for already-Paid bills were taken off the open ones again).
 *
 * NOW: the OUTSTANDING TOTAL per party is its real ledger balance as of the
 * chosen date (exactly what Customer / Vendor Balances and the Ledger show).
 * That balance is then spread over the party's invoices NEWEST FIRST — under the
 * normal "oldest bill is paid first" rule, what is still owed is the most recent
 * bills. Anything left after all invoices (opening balances without an invoice,
 * journal debits, etc.) is shown as one "Opening / other balance" line dated at
 * the start of the financial year.
 *
 * So aging totals ALWAYS equal the ledger, whatever happened on the documents.
 */
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import { recordReads } from './readTelemetry';

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const LIVE_DOC_STATUSES = new Set(['Posted', 'Partial', 'Paid']);

const chunk = (arr, size) => {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
};

/**
 * Ledger balance per party as of `asOfDate` for the selected financial year.
 * The year's opening-balance rows carry everything from earlier years.
 * @param {'customer'|'vendor'} entityType
 * @returns {Promise<{ balances: Object<string, number>, accounts: Array }>}
 *   balances[partyId] = receivable (customer: Dr−Cr) or payable (vendor: Cr−Dr)
 */
export async function loadPartyLedgerBalances(companyId, { entityType, fyId, asOfDate, branchId }) {
  const accSnap = await getDocs(query(
    collection(db, 'companies', companyId, 'accounts'),
    where('linkedEntityType', '==', entityType)
  ));
  recordReads(companyId, accSnap.size);
  const accounts = accSnap.docs.map((d) => ({ id: d.id, ...d.data() })).filter((a) => a.linkedEntityId);
  const accToParty = {};
  accounts.forEach((a) => { accToParty[a.id] = a.linkedEntityId; });

  const balances = {};
  if (!fyId || accounts.length === 0) return { balances, accounts };

  const subRef = collection(db, 'companies', companyId, 'subTransactions');
  const rows = [];
  // Only this party type's own ledger lines — never the whole year's ledger.
  for (const ids of chunk(accounts.map((a) => a.id), 30)) {
    // eslint-disable-next-line no-await-in-loop
    const snap = await getDocs(query(subRef,
      where('financialYearId', '==', fyId),
      where('status', '==', 'Posted'),
      where('accountId', 'in', ids)
    ));
    recordReads(companyId, snap.size);
    snap.docs.forEach((d) => rows.push(d.data()));
  }
  // Opening balances may still be in Draft — they count in every ledger report.
  const obSnap = await getDocs(query(subRef,
    where('financialYearId', '==', fyId),
    where('status', '==', 'Draft'),
    where('tranTypeCode', '==', 'OB')
  ));
  recordReads(companyId, obSnap.size);
  obSnap.docs.forEach((d) => { const r = d.data(); if (accToParty[r.accountId]) rows.push(r); });

  rows.forEach((r) => {
    const partyId = accToParty[r.accountId];
    if (!partyId) return;
    if (asOfDate && (r.docDate || '') > asOfDate) return;
    if (branchId && branchId !== 'all' && r.branchId !== branchId) return;
    const dr = Number(r.debitAmount) || 0;
    const cr = Number(r.creditAmount) || 0;
    balances[partyId] = (balances[partyId] || 0) + (entityType === 'customer' ? dr - cr : cr - dr);
  });
  Object.keys(balances).forEach((k) => { balances[k] = round2(balances[k]); });
  return { balances, accounts };
}

/**
 * Invoices that can carry an outstanding balance: every live invoice of the
 * selected year, plus earlier-year invoices still marked open.
 */
export async function loadAgingInvoices(companyId, { codes, fyId, asOfDate, branchId }) {
  const ref = collection(db, 'companies', companyId, 'transactions');
  const [fySnap, openSnap] = await Promise.all([
    fyId
      ? getDocs(query(ref, where('financialYearId', '==', fyId), where('tranTypeCode', 'in', codes)))
      : Promise.resolve({ docs: [], size: 0 }),
    getDocs(query(ref, where('tranTypeCode', 'in', codes), where('status', 'in', ['Posted', 'Partial']))),
  ]);
  recordReads(companyId, (fySnap.size || 0) + (openSnap.size || 0));
  const byId = new Map();
  [...fySnap.docs, ...openSnap.docs].forEach((d) => {
    const t = { id: d.id, ...d.data() };
    if (!LIVE_DOC_STATUSES.has(t.status)) return;
    if (!t.partyId) return;
    if (asOfDate && (t.docDate || '') > asOfDate) return;
    if (branchId && branchId !== 'all' && t.branchId !== branchId) return;
    byId.set(t.id, t);
  });
  return [...byId.values()];
}

/** Amount this document put on the party's ledger account. */
export function partyAmountOf(t) {
  if (t.tranTypeCode === 'IPI') return round2(t.baseSubtotal ?? t.subtotal ?? 0); // Cr foreign vendor = base value
  return round2(t.netAmount ?? t.totalAmount ?? 0);
}

/**
 * Spread a party's ledger balance over its invoices, newest first.
 * @returns {{ items: Array, openingRemainder: number }}
 *   items: invoices (oldest first) with `outstanding` set (0 when settled)
 */
export function allocateBalanceNewestFirst(balance, invoices) {
  const sortedNewest = [...invoices].sort((a, b) =>
    (b.docDate || '').localeCompare(a.docDate || '') || (b.docNumber || '').localeCompare(a.docNumber || ''));
  let remaining = round2(balance);
  const items = sortedNewest.map((inv) => {
    const amt = Math.max(0, partyAmountOf(inv));
    const portion = remaining > 0 ? Math.min(remaining, amt) : 0;
    remaining = round2(remaining - portion);
    return { ...inv, invoiceAmount: amt, outstanding: round2(portion) };
  });
  return { items: items.reverse(), openingRemainder: remaining > 0.009 ? remaining : 0 };
}

export const OPENING_ITEM_ID = '__opening_balance__';

/** Synthetic line for balance not covered by any invoice. */
export function openingItem(amount, dateStr, label = 'Opening / other balance') {
  return {
    id: OPENING_ITEM_ID,
    docNumber: label,
    docDate: dateStr,
    tranTypeCode: 'OB',
    invoiceAmount: round2(amount),
    netAmount: round2(amount),
    totalAmount: round2(amount),
    outstanding: round2(amount),
    isOpeningLine: true,
  };
}
